/**
 * FieldsManagementView — table view for all fields with filters and actions.
 * Sprint Block C Phase C-f.
 *
 * Props:
 *   token         — JWT
 *   onCreateField — () => void
 *   onEditField   — (field) => void
 *   onDeleteField — (field) => void
 *   onOpenDetail  — (fieldId, fieldName) => void
 */
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const FILTER_KEYS = [
  { value: 'all',          i18nKey: 'field.filterAll' },
  { value: 'with_polygon', i18nKey: 'field.filterWithPolygon' },
  { value: 'no_polygon',   i18nKey: 'field.filterNoPolygon' },
  { value: 'with_crop',    i18nKey: 'field.filterWithCrop' },
  { value: 'no_crop',      i18nKey: 'field.filterNoCrop' },
];

function formatHa(ha) {
  if (!ha) return '–';
  return `${Number(ha).toLocaleString('de-DE', { maximumFractionDigits: 2 })} ha`;
}

function StatusDot({ status }) {
  const colors = { good: '#22c55e', watch: '#f59e0b', problem: '#ef4444' };
  const color = colors[status] ?? '#9E9E9E';
  return (
    <span
      className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
      style={{ backgroundColor: color }}
    />
  );
}

export default function FieldsManagementView({
  token,
  onCreateField,
  onEditField,
  onDeleteField,
  onOpenDetail,
  onImportFields,
}) {
  const { t } = useTranslation();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const fetchFields = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/aif/farm/fields', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setFields(data.fields ?? []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchFields(); }, [fetchFields]);

  const filtered = fields.filter(f => {
    if (search && !f.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (filter === 'with_polygon') return !!f.has_polygon;
    if (filter === 'no_polygon')   return !f.has_polygon;
    if (filter === 'with_crop')    return !!f.current_crop;
    if (filter === 'no_crop')      return !f.current_crop;
    return true;
  });

  const totalArea = filtered.reduce((sum, f) => sum + (Number(f.area_ha) || 0), 0);

  return (
    <div data-testid="fields-management-view" className="flex flex-col h-full bg-[var(--aif-bg-page)]">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-[var(--aif-border-default)]">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold text-[var(--aif-text-primary)]">
              {t('field.manageTitle')}
            </h2>
            <p className="text-xs text-[var(--aif-text-muted)]">
              {t('field.manageSummary', { count: filtered.length, area: formatHa(totalArea) })}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="fields-add-btn"
              onClick={onCreateField}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[var(--aif-color-primary)] text-white text-sm font-medium
                         hover:opacity-90 transition"
            >
              <span>+</span> {t('field.newField')}
            </button>
            <button
              data-testid="fields-import-btn"
              onClick={onImportFields}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[var(--aif-border-default)]
                         text-[var(--aif-text-primary)] text-sm font-medium hover:bg-[var(--aif-bg-elevated)] transition"
            >
              <span>📂</span> {t('field.import')}
            </button>
          </div>
        </div>

        {/* Search + Filter */}
        <div className="flex gap-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('ui.fieldSearch')}
            className="flex-1 px-3 py-2 rounded-xl border border-[var(--aif-border-default)]
                       bg-[var(--aif-bg-elevated)] text-[var(--aif-text-primary)] text-sm
                       focus:outline-none focus-visible:ring-2 focus:ring-[var(--aif-color-primary)]"
          />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-2 rounded-xl border border-[var(--aif-border-default)]
                       bg-[var(--aif-bg-elevated)] text-[var(--aif-text-primary)] text-sm
                       focus:outline-none"
          >
            {FILTER_KEYS.map(o => <option key={o.value} value={o.value}>{t(o.i18nKey)}</option>)}
          </select>
        </div>
      </div>

      {/* Body */}
      {loading && (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-7 h-7 border-4 border-[var(--aif-border-default)] border-t-[var(--aif-color-primary)] rounded-full animate-spin" />
        </div>
      )}

      {!loading && error && (
        <div className="flex-1 flex items-center justify-center px-6">
          <p className="text-sm text-red-500">{error}</p>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center space-y-3">
            <div className="text-3xl">🌾</div>
            <p className="text-sm text-[var(--aif-text-muted)]">
              {search || filter !== 'all'
                ? t('field.noFieldsFiltered')
                : t('ui.noFields')}
            </p>
            {!search && filter === 'all' && (
              <button
                onClick={onCreateField}
                className="text-sm text-[var(--aif-color-primary)] underline"
              >
                {t('field.createFirst')}
              </button>
            )}
          </div>
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="flex-1 overflow-y-auto">
          {/* Table header */}
          <div className="sticky top-0 bg-[var(--aif-bg-elevated)] border-b border-[var(--aif-border-default)]
                          grid grid-cols-[1fr_auto_auto_auto_auto_auto] gap-2 px-4 py-2 text-[10px] font-semibold
                          text-[var(--aif-text-muted)] uppercase tracking-wide">
            <span>{t('field.colName')}</span>
            <span className="text-right">{t('field.colArea')}</span>
            <span>{t('field.colCrop')}</span>
            <span>{t('field.colSoilType')}</span>
            <span className="text-center">{t('field.colSoilQuality')}</span>
            <span className="text-center">{t('field.colStatus')}</span>
          </div>

          {/* Rows */}
          {filtered.map(f => (
            <div
              key={f.id}
              data-testid={`field-row-${f.id}`}
              className="grid grid-cols-[1fr_auto_auto_auto_auto_auto] gap-2 px-4 py-3
                         border-b border-[var(--aif-border-default)] hover:bg-[var(--aif-bg-elevated)]
                         transition items-center group"
            >
              <button
                className="text-left"
                onClick={() => onOpenDetail?.(f.id, f.name)}
              >
                <div className="text-sm font-medium text-[var(--aif-text-primary)] truncate">
                  {f.has_polygon ? '📐' : '📍'} {f.name}
                </div>
              </button>
              <span className="text-sm text-[var(--aif-text-secondary)] text-right whitespace-nowrap">
                {formatHa(f.area_ha)}
              </span>
              <span className="text-sm text-[var(--aif-text-secondary)] truncate max-w-[80px]">
                {f.current_crop_name ?? '–'}
              </span>
              <span className="text-sm text-[var(--aif-text-secondary)] truncate max-w-[70px]">
                {f.soil_type ?? '–'}
              </span>
              <span className="text-sm text-[var(--aif-text-secondary)] text-center">
                {f.soil_quality_index ?? '–'}
              </span>
              <div className="flex items-center justify-center gap-1">
                <StatusDot status={f.status} />
                {/* Action buttons on hover */}
                <div className="hidden group-hover:flex items-center gap-1 ml-1">
                  <button
                    data-testid={`field-edit-${f.id}`}
                    onClick={() => onEditField?.(f)}
                    title={t('ui.edit')}
                    className="p-1 rounded text-[var(--aif-text-muted)] hover:text-[var(--aif-text-primary)] hover:bg-[var(--aif-bg-page)] text-sm"
                  >
                    ✏️
                  </button>
                  <button
                    data-testid={`field-delete-${f.id}`}
                    onClick={() => onDeleteField?.(f)}
                    title={t('ui.delete')}
                    className="p-1 rounded text-[var(--aif-text-muted)] hover:text-red-500 hover:bg-[var(--aif-bg-page)] text-sm"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
